// ═══════════════════════════════════════════════════════════════════════════
// Adaptadores Postgres ⇄ forma de la app — Etapa 4a
//
// La app usa los nombres del sistema viejo (grossPrice, fiscalStreet,
// pricingRule) y Postgres los del modelo nuevo (base_price, fiscal_street,
// price_list_id). Estos adaptadores traducen en ambos sentidos.
//
// POR QUÉ UNA CAPA Y NO RENOMBRAR TODO DE GOLPE: renombrar en los 30 módulos
// a la vez sería un cambio enorme sin red de seguridad. Con adaptadores, la
// fuente de datos cambia SIN que la interfaz se entere, y el renombrado —si
// se hace— pasa a ser una limpieza posterior, opcional y por partes.
//
// La prueba que los valida es fuerte: leer de Postgres, adaptar, y comprobar
// que sale EXACTAMENTE lo que la app ya tenía. Si coincide, cambiar la fuente
// es invisible.
//
// El campo `legacy_id` es la costura: conserva el id del sistema viejo (c1,
// p3, id_177…) para que las referencias existentes sigan resolviendo.
// ═══════════════════════════════════════════════════════════════════════════

import { claveFormaPago, claveMetodoPago, claveUsoCfdi } from "../fiscal/cfdi.js";

/** Fila de `client` en Postgres → cliente con la forma que espera la app. */
export function clienteDesdePG(fila) {
  return {
    id: fila.legacy_id || fila.id,
    name: fila.name || "",
    legalName: fila.legal_name || undefined,
    rfc: fila.rfc || "",
    curp: fila.curp || undefined,
    email: fila.email || "",
    phone: fila.phone || "",
    att: fila.att || "",

    street: fila.street || "",
    colonia: fila.colonia || "",
    cp: fila.cp || "",
    city: fila.city || "",
    state: fila.state || "",

    fiscalStreet: fila.fiscal_street || undefined,
    fiscalColonia: fila.fiscal_colonia || undefined,
    domicilioFiscalZip: fila.fiscal_cp || undefined,
    fiscalCity: fila.fiscal_city || undefined,
    fiscalState: fila.fiscal_state || undefined,
    taxSystem: fila.regimen_fiscal || undefined,
    // TODOS los que trae su CSF. El lector de CSF los sacaba desde siempre y
    // al guardar se quedaba el primero: la app sabía que había cuatro, lo
    // decía en pantalla, y después olvidaba tres (migración 0061).
    regimenes: Array.isArray(fila.regimenes) ? fila.regimenes : [],

    // price_list.code viene del join; sin él se asume la lista estándar.
    pricingRule: fila.price_list?.code || fila.price_list_code || "default",
    discount: num(fila.discount, 0),
    ivaRate: num(fila.iva_rate, 16),
    // Siempre claves del SAT, aunque la fila traiga una leyenda (0066).
    cfdiUse: claveUsoCfdi(fila.cfdi_use),
    paymentMethod: claveFormaPago(fila.payment_method),
    paymentTiming: claveMetodoPago(fila.payment_timing),

    // Campos nuevos que la app todavía no usa, pero que ya viajan.
    creditDays: fila.credit_days ?? 0,
    iepsDisplay: fila.ieps_display || "incluido",
    _pgId: fila.id,          // uuid real, para escribir en la Etapa 4b
  };
}

/** Fila de `product` en Postgres → producto con la forma que espera la app. */
export function productoDesdePG(fila, stockPorProducto = null) {
  return {
    id: fila.legacy_id || fila.id,
    name: fila.name || "",
    grossPrice: num(fila.base_price, 0),
    netPrice: fila.list_price == null ? undefined : num(fila.list_price, 0),
    category: fila.category || "",
    gl: fila.gl == null ? undefined : num(fila.gl, 0),
    iepsRate: num(fila.ieps_rate, 0),
    // El stock ya no es una columna: es la suma de movimientos. Si no se
    // proporciona el mapa, se deja en 0 en vez de inventar un número.
    stock: stockPorProducto ? num(stockPorProducto.get(fila.id), 0) : 0,
    description: fila.description || "",
    tastingNotes: fila.tasting_notes || "",
    imageBase64: fila.image_url || "",

    // Los tres lados de la botella (migración 0053). Se leen y se escriben en
    // el mismo commit, a propósito: el defecto de `image_url` fue exactamente
    // tener lector sin escritor, y nadie lo notó hasta que Postgres pasó a
    // mandar y once fotos se apagaron de golpe.
    pairing: fila.pairing || "",
    labelStory: fila.label_story || "",
    workTitle: fila.work_title || "",
    workAuthor: fila.work_author || "",
    workKind: fila.work_kind || "",
    workYear: fila.work_year || "",
    workNote: fila.work_note || "",
    artistName: fila.artist_name || "",
    artistNote: fila.artist_note || "",
    artworkNote: fila.artwork_note || "",

    type: fila.type || "alcohol",
    satProductKey: fila.sat_product_key || undefined,
    satUnitKey: fila.sat_unit_key || undefined,
    _pgId: fila.id,
  };
}

/**
 * Listas de precio de Postgres → la forma de PRICING_RULES que usa la app.
 * Devuelve { código: { label, priceMap } }, con priceMap indexado por el id
 * LEGADO del producto, que es el que la app maneja.
 */
export function preciosDesdePG(listas, renglones, productosPorId) {
  const reglas = {};
  for (const l of listas) {
    reglas[l.code] = { label: l.label, priceMap: null, _pgId: l.id };
  }
  for (const r of renglones) {
    const lista = listas.find(l => l.id === r.price_list_id);
    if (!lista) continue;
    const legado = productosPorId.get(r.product_id);
    if (!legado) continue;
    const regla = reglas[lista.code];
    if (!regla.priceMap) regla.priceMap = {};
    regla.priceMap[legado] = num(r.price, 0);
  }
  return reglas;
}

/** Precios especiales por cliente → { legacyProductId: precio }. */
export function preciosClienteDesdePG(renglones, productosPorId) {
  const porCliente = new Map();
  for (const r of renglones) {
    const legado = productosPorId.get(r.product_id);
    if (!legado) continue;
    if (!porCliente.has(r.client_id)) porCliente.set(r.client_id, {});
    porCliente.get(r.client_id)[legado] = num(r.price, 0);
  }
  return porCliente;
}

/**
 * numeric de Postgres llega como CADENA ("443.0000") para no perder precisión.
 * Convertirlo mal es la clase de error que descuadra un total sin avisar.
 */
function num(v, porOmision = 0) {
  if (v === null || v === undefined || v === "") return porOmision;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : porOmision;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sentido inverso: app → Postgres (Etapa 4b, escritura)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cliente de la app → fila de `client` para Postgres.
 *
 * `pricingRule` NO se traduce aquí: la lista de precios es una FK que hay que
 * resolver contra price_list, y eso exige consultar. Se pasa el código y quien
 * escribe lo resuelve. Adivinar un uuid aquí produciría claves rotas.
 * De eso se encarga `guardar_cliente()` (migración 0012), que además resuelve
 * los precios especiales — otra cosa que no cabe en una fila.
 */
export function clienteHaciaPG(c, tenantId) {
  return {
    tenant_id: tenantId,
    legacy_id: c.id,                 // la costura con el sistema viejo
    name: c.name || "",
    legal_name: vacioANulo(c.legalName),
    rfc: vacioANulo(c.rfc),
    curp: vacioANulo(c.curp),
    email: vacioANulo(c.email),
    phone: vacioANulo(c.phone),
    att: vacioANulo(c.att),
    street: vacioANulo(c.street),
    colonia: vacioANulo(c.colonia),
    cp: vacioANulo(c.cp),
    city: vacioANulo(c.city),
    state: vacioANulo(c.state),
    fiscal_street: vacioANulo(c.fiscalStreet),
    fiscal_colonia: vacioANulo(c.fiscalColonia),
    fiscal_cp: vacioANulo(c.domicilioFiscalZip),
    fiscal_city: vacioANulo(c.fiscalCity),
    fiscal_state: vacioANulo(c.fiscalState),
    regimen_fiscal: vacioANulo(c.taxSystem),
    // Se escribe Y se lee, en el mismo cambio. Un campo con lector y sin
    // escritor es el defecto que en este adaptador ya apagó once fotos.
    regimenes: Array.isArray(c.regimenes) && c.regimenes.length ? c.regimenes : null,
    discount: Number(c.discount) || 0,
    iva_rate: Number(c.ivaRate) || 16,
    cfdi_use: vacioANulo(c.cfdiUse),
    payment_method: vacioANulo(c.paymentMethod),
    payment_timing: vacioANulo(c.paymentTiming),
    credit_days: Number(c.creditDays) || 0,
    // Decide cómo se timbra: 'incluido' absorbe el IEPS en el valor unitario.
    // Que viaje importa — es un dato fiscal, no una preferencia de pantalla.
    ieps_display: c.iepsDisplay === "desglosado" ? "desglosado" : "incluido",
  };
}

/**
 * Argumentos de `guardar_cliente()` a partir de un cliente de la app.
 *
 * Existe porque tres cosas del cliente NO son columnas planas y por eso se
 * quedaban en el dispositivo: la lista de precios (FK), los precios especiales
 * (tabla aparte) y el modo de IEPS (enum). Ver la migración 0012.
 */
export function clienteRPC(c, tenantId) {
  const { tenant_id: _t, legacy_id: _l, ...campos } = clienteHaciaPG(c, tenantId);

  // undefined y "" significan "sin precio especial": se omiten para que el
  // servidor los retire. Distinto de no mandar el mapa, que es "no los toques".
  const precios = {};
  for (const [prod, valor] of Object.entries(c.customPrices || {})) {
    if (valor === undefined || valor === null || valor === "") continue;
    const n = Number(valor);
    if (Number.isFinite(n)) precios[prod] = n;
  }

  return {
    p_tenant: tenantId,
    p_legacy: c.id,
    p_campos: campos,
    p_lista: c.pricingRule || "default",
    p_precios: precios,
  };
}

/** Producto de la app → fila de `product` para Postgres. */
export function productoHaciaPG(p, tenantId) {
  return {
    tenant_id: tenantId,
    legacy_id: p.id,
    name: p.name || "",
    type: p.type || "alcohol",
    category: vacioANulo(p.category),
    base_price: Number(p.grossPrice) || 0,
    list_price: p.netPrice == null ? null : Number(p.netPrice),
    gl: p.gl == null ? null : Number(p.gl),
    ieps_rate: Number(p.iepsRate) || 0,
    sat_product_key: vacioANulo(p.satProductKey),
    sat_unit_key: vacioANulo(p.satUnitKey),
    description: vacioANulo(p.description),
    tasting_notes: vacioANulo(p.tastingNotes),
    // La foto. FALTABA, y el lector sí la traía (`imageBase64: fila.image_url`),
    // así que el viaje era de ida y no de vuelta: se subía una foto, se veía en
    // la pantalla, se guardaba... y se descartaba aquí en silencio. Nadie se
    // enteró mientras el dispositivo era la fuente, porque la copia local sí la
    // conservaba. El día que Postgres pasó a mandar, las 11 fotos se apagaron
    // de golpe.
    //
    // Peor que faltar: el formulario seguía aceptándolas y enseñando la vista
    // previa. Parecía que funcionaba.
    image_url: vacioANulo(p.imageBase64),

    // El otro sentido de los tres lados de la botella. Ver el lector.
    pairing: vacioANulo(p.pairing),
    label_story: vacioANulo(p.labelStory),
    work_title: vacioANulo(p.workTitle),
    work_author: vacioANulo(p.workAuthor),
    work_kind: vacioANulo(p.workKind),
    work_year: vacioANulo(p.workYear),
    work_note: vacioANulo(p.workNote),
    artist_name: vacioANulo(p.artistName),
    artist_note: vacioANulo(p.artistNote),
    artwork_note: vacioANulo(p.artworkNote),

    // ⚠ `stock` NO se escribe: es la suma de inventory_movement. Mandarlo
    //   aquí sería reintroducir el campo mutable que el modelo eliminó.
  };
}

/** "" y undefined → null. Postgres distingue vacío de ausente; la app no. */
function vacioANulo(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

/**
 * Remisión de Postgres → la forma que usa la app.
 *
 * Con esto las remisiones dejan de venir del snapshot local. Importa más de lo
 * que parece: mientras se leían de localStorage, TODOS veían TODAS — un
 * vendedor incluido. La RLS ya filtra por cartera, pero no servía de nada si
 * la app nunca preguntaba a la base.
 *
 * Los importes se toman TAL CUAL de la remisión, no se recalculan: el
 * documento manda. Recalcularlos aquí abriría la puerta a que la pantalla
 * muestre un total distinto al que se timbró.
 */
export function remisionDesdePG(fila, { lineas = [], cfdi = null, pagos = [],
                                        legadoPorUuid = new Map(),
                                        legadoClientePorUuid = new Map(),
                                        repPorPago = new Map() } = {}) {
  // ── El snapshot viene en DOS FORMAS, y hay que entender las dos ───────────
  //
  // Las remisiones que migraron del monolito guardaron el cliente con la forma
  // de la APP —`legalName`, `ivaRate`, `taxSystem`, `domicilioFiscalZip`—. Las
  // que crea `crear_remision` guardan la fila de POSTGRES tal cual:
  // `legal_name`, `iva_rate`, `regimen_fiscal`, `fiscal_cp`.
  //
  // Este adaptador solo leía la segunda. En producción eso son 43 remisiones de
  // 46 leyéndose mal, calladas:
  //
  //   · `ivaRate` caía al 16 por omisión cuando el snapshot decía 8. Ensenada
  //     es frontera: el 8 es el bueno. El total impreso no cambia —sale de las
  //     columnas de la remisión, no de aquí— pero el dato del cliente mentía.
  //   · `legalName`, `taxSystem` y el domicilio fiscal salían vacíos, que es lo
  //     que decide si una remisión "tiene CSF" para poder timbrarse.
  //   · `cfdiUse`, `paymentMethod` y `paymentTiming` quedaban en blanco.
  //
  // Se leen las dos, la nueva primero. Sin reescribir un solo snapshot: son
  // documentos, y dicen lo que dijeron.
  const snap = fila.client_snapshot || {};
  const s = (nuevo, viejo) => (snap[nuevo] !== undefined && snap[nuevo] !== null)
    ? snap[nuevo] : snap[viejo];

  return {
    id: fila.legacy_id || fila.id,
    folio: fila.folio,
    date: fila.issued_at,

    // El snapshot congelado, con la forma vieja: es lo que el PDF imprime y
    // debe seguir diciendo lo que decía el día que se emitió.
    client: {
      // ── Con qué id se reconoce al cliente de esta remisión ────────────────
      //
      // El snapshot está CONGELADO a propósito: es lo que el PDF imprimió ese
      // día. Pero las remisiones viejas se guardaron sin `legacy_id` dentro —
      // el campo se empezó a escribir después—, así que aquí se caía al uuid de
      // Postgres mientras la lista de clientes usa el id legado. Dos nombres
      // para la misma persona, y nada casaba.
      //
      // Se notó en Cuentas, que agrupa las remisiones por `client.id`: de 46
      // remisiones aparecían TRES, justo las tres cuyo snapshot sí traía el
      // campo. Historial las mostraba todas porque no agrupa por cliente.
      //
      // Se resuelve AQUÍ, traduciendo el uuid, y no reescribiendo los 43
      // snapshots: corregir documentos históricos para arreglar una pantalla es
      // exactamente lo que este proyecto no hace. El snapshot sigue diciendo lo
      // que decía.
      id: snap.legacy_id || legadoClientePorUuid.get(fila.client_id) || fila.client_id,
      name: snap.name || "",
      legalName: s("legal_name", "legalName") || undefined,
      rfc: snap.rfc || "", email: snap.email || "", phone: snap.phone || "",
      att: snap.att || "",
      street: snap.street || "", colonia: snap.colonia || "", cp: snap.cp || "",
      city: snap.city || "", state: snap.state || "",
      fiscalStreet: s("fiscal_street", "fiscalStreet") || undefined,
      fiscalColonia: s("fiscal_colonia", "fiscalColonia") || undefined,
      domicilioFiscalZip: s("fiscal_cp", "domicilioFiscalZip") || undefined,
      fiscalCity: s("fiscal_city", "fiscalCity") || undefined,
      fiscalState: s("fiscal_state", "fiscalState") || undefined,
      taxSystem: s("regimen_fiscal", "taxSystem") || undefined,
      discount: num(snap.discount, 0),
      ivaRate: num(s("iva_rate", "ivaRate"), 16),
      cfdiUse: claveUsoCfdi(s("cfdi_use", "cfdiUse")),
      paymentMethod: claveFormaPago(s("payment_method", "paymentMethod")),
      paymentTiming: claveMetodoPago(s("payment_timing", "paymentTiming")),
    },

    lines: lineas.map(l => ({
      productId: legadoPorUuid.get(l.product_id) || l.product_id,
      productName: l.description || "",
      qty: num(l.qty, 0),
      unitPrice: num(l.unit_price, 0),
      amount: num(l.line_total, 0),
      // La cortesía del renglón. Se lee Y se escribe —ver
      // `argumentosDeRemision`— porque un campo con lector y sin escritor es
      // el defecto que en este adaptador ya apagó once fotos.
      cortesia: num(l.courtesy_qty, 0),
      iepsRate: num(l.ieps_rate, 0),
    })),

    discount: num(fila.discount, 0),
    ivaRate: num(fila.iva_rate, 16),
    subtotalBeforeDiscount: num(fila.subtotal_before_disc, 0),
    regimenFiscal: fila.regimen_fiscal || undefined,
    // De qué bodega salió. Hace falta para EDITAR: sin él, el editor proponía
    // la bodega recordada del aparato y una corrección de cantidades movía,
    // sin que nadie lo pidiera, la mercancía de bodega (0064).
    warehouse: fila.warehouse_id || undefined,
    discountAmount: num(fila.discount_amount, 0),
    subtotal: num(fila.subtotal, 0),
    iepsAmount: num(fila.ieps_amount, 0),
    iepsBreakdown: fila.ieps_breakdown || [],
    ivaAmount: num(fila.iva_amount, 0),
    total: num(fila.total, 0),
    totalBottles: num(fila.total_bottles, 0),
    notes: fila.notes || "",
    formaPago: claveFormaPago(fila.forma_pago),
    metodoPago: claveMetodoPago(fila.metodo_pago),
    usoCfdi: claveUsoCfdi(fila.uso_cfdi),
    // Cómo se presentó (o se presentará) el IEPS en su CFDI.
    iepsDisplay: fila.ieps_display === "desglosado" ? "desglosado" : "incluido",

    // El CFDI, si lo hay. `valid` y no `vigente`: es lo que la app compara, y
    // lo que traen las 22 remisiones que ya existían.
    ...(cfdi ? {
      cfdiId: cfdi.facturapi_id || undefined,
      cfdiUuid: cfdi.uuid_sat || undefined,
      cfdiStatus: cfdi.status === "cancelado" ? "canceled" : "valid",
      // Vigente con motivo = se pidió cancelarlo y el receptor no ha aceptado.
      cfdiCancelacionPendiente: cfdi.status !== "cancelado" && !!cfdi.cancel_motive,
      cfdiVerificationUrl: cfdi.verification_url || undefined,
      cfdiTotal: cfdi.total == null ? undefined : num(cfdi.total, 0),
    } : {}),

    // ── Los pagos, CON LOS NOMBRES QUE USA LA APP ────────────────────────
    //
    // Entregaban `amount`, `date`, `method`… y toda la app lee `monto`,
    // `fecha`, `metodo`. O sea que un pago traído de Postgres NO LO CONTABA
    // NADIE: `totalPagado` sumaba `p.monto`, que venía undefined, y la
    // remisión aparecía como pendiente por mucho que estuviera cobrada.
    //
    // Nadie lo notó porque producción tiene cero pagos capturados. En beta,
    // con 53 sembrados, la cobranza entera mentía.
    //
    // Es la TERCERA vez que el adaptador rompe algo así —la foto que no se
    // escribía, el snapshot en dos formas— y por eso ahora hay una prueba de
    // ida y vuelta que compara campo por campo lo que se lee contra lo que la
    // app espera.
    payments: pagos.map(p => ({
      id: p.legacy_id || p.id,
      monto: num(p.amount, 0),
      fecha: p.occurred_at,
      metodo: p.method || "",
      cuenta: p.account_id || "",
      nota: p.note || "",
      // Cortesía o demostración: cierra el saldo y no es cobro (0033).
      kind: p.kind || "dinero",
      ...(p.motivo ? { motivo: p.motivo } : {}),
      revierteA: p.reverses_id || undefined,
      // El complemento de pago, si ya se emitió. Sin esto se emitía y no había
      // dónde verlo: quedaba en la base y la pantalla no se enteraba.
      ...(repPorPago.get(p.id) ? { rep: repPorPago.get(p.id) } : {}),
    })),

    ...(fila.canceled_at ? {
      canceledAt: fila.canceled_at,
      cancelReason: fila.cancel_reason || "",
    } : {}),
  };
}
